import React, { useState, useEffect, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Toast, 
  ToastContainer,
  InputGroup,
  ProgressBar,
  Modal
} from "react-bootstrap";
import {
  ArrowLeft,
  Save,
  Building,
  Phone,
  Envelope,
  Bank,
  Cash,
  Shield,
  CheckCircle,
  XCircle,
  ExclamationCircle,
  Upload,
  Trash,
  BuildingAdd,
  FileText,
  Calendar,
  Download,
  FilePdf,
  Image,
  Globe2,
  Telephone,
  Key
} from "react-bootstrap-icons";

// Configuration API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Obtenir le token d'authentification
const getAuthToken = () => {
  const token = sessionStorage.getItem("authToken");
  return token;
};

// Headers pour les requêtes API
const getHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export default function NouvelleEntite() {
  const navigate = useNavigate();
  
  // États pour les notifications
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  
  // États pour le chargement
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // États pour les pays
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState({
    code: 'GA',
    name: 'Gabon',
    currency_code: 'XAF',
    currency_name: 'Franc CFA',
    currency_symbol: 'FCFA',
    phone_code: '+241'
  });
  
  // FormData
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    pays: 'Gabon',
    country_code: 'GA',
    admin_email: '',
    admin_lname: '',
    admin_fname: '',
    admin_phone: '',
    admin_fonction: '',
    code: '',
    type: 'entreprise',
    ville: '',
    devise: 'XAF',
    statut: 'active',
    dateCreation: new Date().toISOString().split('T')[0],
  });
  
  // États pour les fichiers
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // États pour les modales
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdEntity, setCreatedEntity] = useState(null);
  const [createdAdmin, setCreatedAdmin] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [generatedUsername, setGeneratedUsername] = useState('');
  const [isGeneratingDocs, setIsGeneratingDocs] = useState(false);
  const [generatedDocType, setGeneratedDocType] = useState('');
  
  // États pour la génération de code
  const [generatedCode, setGeneratedCode] = useState('');
  
  // Types d'entités
  const typesEntite = [
    { value: 'entreprise', label: 'Entreprise', icon: <Building className="me-2" />, description: 'Société commerciale ou industrielle' },
    { value: 'commune', label: 'Commune', icon: <Bank className="me-2" />, description: 'Collectivité territoriale' },
    { value: 'organisation', label: 'Organisation', icon: <Cash className="me-2" />, description: 'ONG, Association, etc.' }
  ];

  // Fonctions pour les notifications
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = { id, message, type, show: true };
    setNotifications(prev => [notification, ...prev]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, duration);
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        setAlertMessage({ show: false, message: '', type: 'info' });
      }, duration);
    }
  };

  // Fonction pour charger les pays
  const fetchCountries = useCallback(async () => {
    setLoadingCountries(true);
    setCountriesError(null);
    
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${API_BASE_URL}/countries`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.code === 200 && Array.isArray(data.countries)) {
        setCountries(data.countries);
        
        const gabon = data.countries.find(c => c.iso_alpha2 === 'GA');
        if (gabon) {
          setSelectedCountry({
            code: gabon.iso_alpha2,
            name: gabon.name_fr,
            currency_code: gabon.currency_code,
            currency_name: gabon.currency_name,
            currency_symbol: gabon.currency_symbol,
            phone_code: gabon.phone_code
          });
          
          setFormData(prev => ({
            ...prev,
            pays: gabon.name_fr,
            country_code: gabon.iso_alpha2,
            devise: gabon.currency_code,
            telephone: prev.telephone || gabon.phone_code + ' ',
            admin_phone: prev.admin_phone || gabon.phone_code + ' '
          }));
        }
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (error) {
      console.error('Erreur chargement pays:', error);
      setCountriesError(error.message);
      showAlert('Erreur de chargement de la liste des pays', 'error');
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  // Gestion du changement de pays
  const handlePaysChange = (e) => {
    const paysName = e.target.value;
    if (!paysName) return;
    
    const country = countries.find(c => c.name_fr === paysName);
    
    if (country) {
      setSelectedCountry({
        code: country.iso_alpha2,
        name: country.name_fr,
        currency_code: country.currency_code,
        currency_name: country.currency_name,
        currency_symbol: country.currency_symbol,
        phone_code: country.phone_code
      });
      
      setFormData(prev => ({
        ...prev,
        pays: country.name_fr,
        country_code: country.iso_alpha2,
        devise: country.currency_code,
        telephone: country.phone_code + ' ',
        admin_phone: country.phone_code + ' '
      }));
    }
  };

  // Gestion des changements de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Génération de code
  const genererCodeEntite = () => {
    const prefix = formData.type.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString().slice(-4);
    const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    const code = `${prefix}${timestamp}${random}`;
    
    setGeneratedCode(code);
    setFormData(prev => ({ ...prev, code }));
  };

  // Gestion du logo
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showAlert('Le fichier est trop volumineux (max 5MB)', 'error');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        showAlert('Veuillez sélectionner une image', 'error');
        return;
      }
      
      setLogoFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
      
      showNotification('Logo téléchargé avec succès', 'success');
    }
  };
  
  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    showNotification('Logo supprimé', 'warning');
  };
  
  // Fonction pour générer un document et l'ouvrir dans une nouvelle fenêtre pour impression/sauvegarde PDF
// Fonction pour générer un document et l'ouvrir dans une nouvelle fenêtre pour impression/sauvegarde PDF
const generateAndPrintDocument = (htmlContent, title) => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  // Fonction pour fermer la fenêtre proprement
  const closePrintWindow = () => {
    if (printWindow && !printWindow.closed) {
      printWindow.close();
    }
  };
  
  // Attendre que le contenu soit chargé
  printWindow.onload = () => {
    // Détecter quand la fenêtre perd le focus (annulation ou impression)
    let isPrinting = false;
    
    // Écouter l'événement beforeprint (déclenché quand l'impression commence)
    printWindow.onbeforeprint = () => {
      isPrinting = true;
    };
    
    // Écouter l'événement afterprint (déclenché après impression OU annulation)
    printWindow.onafterprint = () => {
      // Fermer la fenêtre dans tous les cas (que l'impression soit faite ou annulée)
      setTimeout(closePrintWindow, 500);
    };
    
    // Alternative pour les navigateurs qui ne supportent pas bien les événements d'impression
    // Vérifier périodiquement si la boîte de dialogue d'impression est fermée
    const checkPrintDialog = setInterval(() => {
      try {
        // Si la fenêtre est toujours ouverte et que l'impression n'a pas commencé
        if (!isPrinting && printWindow && !printWindow.closed) {
          // Vérifier si la boîte de dialogue d'impression est fermée
          // En essayant d'accéder à un élément (si une erreur se produit, la boîte est fermée)
          try {
            printWindow.document.hasFocus();
          } catch (e) {
            // La boîte de dialogue est fermée (annulation ou impression terminée)
            clearInterval(checkPrintDialog);
            setTimeout(closePrintWindow, 300);
          }
        } else if (isPrinting) {
          // L'impression a commencé, on attend qu'elle se termine
          clearInterval(checkPrintDialog);
          // L'événement afterprint se chargera de fermer
        }
      } catch (e) {
        clearInterval(checkPrintDialog);
        closePrintWindow();
      }
    }, 500);
    
    // Démarrer l'impression
    printWindow.print();
    
    // Fallback : fermer après 10 secondes maximum (au cas où)
    setTimeout(() => {
      clearInterval(checkPrintDialog);
      closePrintWindow();
    }, 10000);
  };
  
  // Gestionnaire d'erreur
  printWindow.onerror = () => {
    closePrintWindow();
  };
};

  // Fonction pour générer le bordereau d'enrôlement
  const generateBordereau = () => {
    setIsGeneratingDocs(true);
    setGeneratedDocType('bordereau');
    
    try {
      const currentDate = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Bordereau d'enrôlement - ${createdEntity?.nom || formData.nom}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Arial', sans-serif;
              padding: 40px;
              background: white;
              color: #333;
            }
            @media print {
              body { padding: 20px; margin: 0; }
              .no-print { display: none; }
              button { display: none; }
            }
                   .header {
    text-align: center;
    margin-bottom: 40px;
    padding: 20px 0;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 16px;
    position: relative;
    overflow: hidden;
  }
  
  .header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0d6efd, #20c997, #0d6efd);
  }
  
  .logo-container {
    margin-bottom: 15px;
  }
  
  .logo-placeholder {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 70px;
    height: 70px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 4px 15px rgba(13, 110, 253, 0.15);
  }
  
  .title-wrapper {
    margin-bottom: 15px;
  }
  
  .title {
    font-size: 28px;
    font-weight: 800;
    background: linear-gradient(135deg, #0d6efd, #0a58ca);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 1px;
    margin-bottom: 8px;
  }
  
  .subtitle {
    font-size: 14px;
    color: #6c757d;
    font-weight: 500;
  }
  
  .badge-container {
    display: flex;
    justify-content: center;
  }
  
  .badge-success {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    color: #155724;
    padding: 6px 16px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid #c3e6cb;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }
          
            .section {
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            .section-title {
              background: #f8f9fa;
              padding: 10px 15px;
              border-left: 4px solid #0d6efd;
              margin-bottom: 15px;
              font-weight: bold;
              font-size: 18px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              padding: 10px;
            }
            .info-item {
              border-bottom: 1px solid #e9ecef;
              padding: 8px 0;
            }
            .info-label {
              font-weight: bold;
              color: #495057;
              font-size: 12px;
              text-transform: uppercase;
              margin-bottom: 4px;
            }
            .info-value {
              font-size: 14px;
              color: #212529;
            }
            .badge-success {
              background: #d4edda;
              color: #155724;
              padding: 4px 12px;
              border-radius: 20px;
              font-size: 12px;
              display: inline-block;
            }
            .footer {
              margin-top: 50px;
              padding-top: 20px;
              border-top: 1px solid #dee2e6;
              text-align: center;
              font-size: 11px;
              color: #6c757d;
            }
            .signature-section {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
            }
            .signature-box {
              text-align: center;
              width: 45%;
            }
            .signature-line {
              border-top: 1px solid #000;
              margin-top: 40px;
              padding-top: 10px;
            }
          </style>
        </head>
        <body>
          <div style="text-align: right; margin-bottom: 20px;" class="no-print">
            <button onclick="window.print()" style="padding: 8px 16px; background: #0d6efd; color: white; border: none; border-radius: 4px; cursor: pointer;">
               Enregistrer en PDF
            </button>
          </div>
          
          <div class="header">

  
  <div class="title-wrapper">
    <div class="title">BORDEREAU D'ENRÔLEMENT</div>
    <div class="subtitle">Document officiel d'enregistrement de l'entité</div>
  </div>
  
  <div class="badge-container">
    <div class="badge-success">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px;">
        <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Généré le ${currentDate}
    </div>
  </div>
</div>

          <div class="section">
            <div class="section-title"> INFORMATIONS DE L'ENTITÉ</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Nom de l'entité</div>
                <div class="info-value"><strong>${createdEntity?.nom || formData.nom}</strong></div>
              </div>
              <div class="info-item">
                <div class="info-label">ID Entité</div>
                <div class="info-value"><code>${createdEntity?.entite_id || 'En cours'}</code></div>
              </div>
              <div class="info-item">
                <div class="info-label">Adresse</div>
                <div class="info-value">${formData.adresse || 'Non spécifiée'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Ville</div>
                <div class="info-value">${formData.ville || 'Non spécifiée'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Pays</div>
                <div class="info-value">${formData.pays}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Téléphone</div>
                <div class="info-value">${formData.telephone}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Email</div>
                <div class="info-value">${formData.email}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Type d'entité</div>
                <div class="info-value">${typesEntite.find(t => t.value === formData.type)?.label || formData.type}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Devise</div>
                <div class="info-value">${selectedCountry.currency_code} - ${selectedCountry.currency_name}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Date d'enrôlement</div>
                <div class="info-value">${currentDate}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title"> INFORMATIONS DE L'ADMINISTRATEUR</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Nom complet</div>
                <div class="info-value">${formData.admin_lname} ${formData.admin_fname}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Email</div>
                <div class="info-value">${formData.admin_email}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Téléphone</div>
                <div class="info-value">${formData.admin_phone}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Fonction</div>
                <div class="info-value">${formData.admin_fonction}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title"> IDENTIFIANTS DE CONNEXION</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Nom d'utilisateur</div>
                <div class="info-value"><code>${generatedUsername}</code></div>
              </div>
              <div class="info-item">
                <div class="info-label">Mot de passe</div>
                <div class="info-value"><code style="background: #f8f9fa; padding: 2px 6px; border-radius: 4px;">${generatedPassword}</code></div>
              </div>
            </div>
          </div>

         
          <div class="footer">
            <p>Document généré automatiquement par le système d'enrôlement</p>
            <p>Ce document fait foi de l'enregistrement officiel de l'entité</p>
          </div>
        </body>
        </html>
      `;
      
      generateAndPrintDocument(htmlContent, `Bordereau d'enrôlement - ${createdEntity?.nom || formData.nom}`);
      showNotification('Bordereau d\'enrôlement ouvert - utilisez "Enregistrer en PDF" pour le sauvegarder', 'success', 5000);
    } catch (error) {
      console.error('Erreur:', error);
      showAlert('Erreur lors de la génération du bordereau', 'error');
    } finally {
      setIsGeneratingDocs(false);
      setGeneratedDocType('');
    }
  };

  // Fonction pour générer le contrat d'enrôlement
  const generateContrat = () => {
    setIsGeneratingDocs(true);
    setGeneratedDocType('contrat');
    
    try {
      const currentDate = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Contrat d'enrôlement - ${createdEntity?.nom || formData.nom}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Times New Roman', serif;
              padding: 50px;
              background: white;
              color: #333;
              line-height: 1.6;
            }
            @media print {
              body { padding: 20px; margin: 0; }
              .no-print { display: none; }
              button { display: none; }
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
            }
            .title {
              font-size: 24px;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 10px;
            }
            .subtitle {
              font-size: 14px;
              color: #666;
            }
            .contract-number {
              text-align: right;
              margin-bottom: 30px;
              font-size: 12px;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-weight: bold;
              font-size: 16px;
              margin-bottom: 15px;
              text-decoration: underline;
            }
            .clause {
              margin-bottom: 15px;
            }
            .signature-section {
              margin-top: 50px;
              display: flex;
              justify-content: space-between;
            }
            .signature-box {
              text-align: center;
              width: 45%;
            }
            .signature-line {
              border-top: 1px solid #000;
              margin-top: 60px;
              padding-top: 10px;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-size: 11px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 20px;
            }
          </style>
        </head>
        <body>
          <div style="text-align: right; margin-bottom: 20px;" class="no-print">
            <button onclick="window.print()" style="padding: 8px 16px; background: #0d6efd; color: white; border: none; border-radius: 4px; cursor: pointer;">
              📄 Enregistrer en PDF
            </button>
          </div>
          
          <div class="contract-number">
            Réf: CONTRAT-${createdEntity?.entite_id || Date.now()}-${new Date().getFullYear()}
          </div>

          <div class="header">
            <div class="title">CONTRAT D'ENRÔLEMENT</div>
            <div class="subtitle">Entre la plateforme SyncTaxe et l'entité enrôlée</div>
          </div>

          <div class="section">
            <div class="section-title">ENTRE LES SOUSSIGNÉS :</div>
            <div class="clause">
              <strong>La plateforme SyncTaxe</strong>, société de services fiscaux, représentée par son directeur général,
              ci-après dénommée "LA PLATEFORME".
            </div>
            <div class="clause" style="margin-top: 15px;">
              <strong>ET</strong>
            </div>
            <div class="clause">
              <strong>L'entité ${createdEntity?.nom || formData.nom}</strong>, représentée par ${formData.admin_lname} ${formData.admin_fname} en qualité de ${formData.admin_fonction},
              ci-après dénommée "L'ENTITÉ".
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 1 : OBJET DU CONTRAT</div>
            <div class="clause">
              Le présent contrat a pour objet de définir les conditions d'enrôlement de l'entité sur la plateforme SyncTaxe,
              ainsi que les droits et obligations des parties dans le cadre de l'utilisation des services fiscaux en ligne.
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 2 : DESCRIPTION DES SERVICES</div>
            <div class="clause">
              La plateforme SyncTaxe met à disposition de l'entité les services suivants :
            </div>
            <div class="clause">
              - Gestion des déclarations fiscales en ligne<br/>
              - Paiement des taxes et impôts<br/>
              - Suivi des échéances fiscales<br/>
              - Génération des bordereaux et quittances<br/>
              - Accès à l'historique des transactions
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 3 : OBLIGATIONS DE L'ENTITÉ</div>
            <div class="clause">
              L'entité s'engage à :
            </div>
            <div class="clause">
              - Fournir des informations exactes et complètes lors de l'enrôlement<br/>
              - Respecter les lois et règlements fiscaux en vigueur<br/>
              - Payer les taxes et impôts dans les délais impartis<br/>
              - Maintenir la confidentialité de ses identifiants de connexion<br/>
              - Utiliser la plateforme conformément aux conditions d'utilisation
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 4 : OBLIGATIONS DE LA PLATEFORME</div>
            <div class="clause">
              La plateforme SyncTaxe s'engage à :
            </div>
            <div class="clause">
              - Assurer la disponibilité et la sécurité des services<br/>
              - Garantir la confidentialité des données de l'entité<br/>
              - Fournir une assistance technique en cas de besoin<br/>
              - Respecter la réglementation sur la protection des données
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 5 : DURÉE DU CONTRAT</div>
            <div class="clause">
              Le présent contrat est conclu pour une durée indéterminée à compter du ${currentDate}.
              Chaque partie peut résilier le contrat à tout moment moyennant un préavis de 30 jours.
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 6 : CONFIDENTIALITÉ</div>
            <div class="clause">
              Les parties s'engagent à maintenir strictement confidentielles toutes les informations
              échangées dans le cadre de l'exécution du présent contrat.
            </div>
          </div>

          <div class="section">
            <div class="section-title">ARTICLE 7 : LOI APPLICABLE ET LITIGES</div>
            <div class="clause">
              Le présent contrat est régi par la loi applicable dans le pays de résidence de l'entité.
              En cas de litige, une solution amiable sera recherchée avant toute action judiciaire.
            </div>
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">
                Pour la plateforme SyncTaxe<br/>
                <small>(Signature précédée de la mention "Lu et approuvé")</small>
              </div>
            </div>
            <div class="signature-box">
              <div class="signature-line">
                Pour l'entité ${createdEntity?.nom || formData.nom}<br/>
                ${formData.admin_lname} ${formData.admin_fname}<br/>
                <small>(Signature précédée de la mention "Lu et approuvé")</small>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>Fait à ${formData.ville || 'Libreville'}, le ${currentDate}</p>
            <p>En deux exemplaires originaux, chaque partie reconnaît avoir reçu le sien.</p>
          </div>
        </body>
        </html>
      `;
      
      generateAndPrintDocument(htmlContent, `Contrat d'enrôlement - ${createdEntity?.nom || formData.nom}`);
      showNotification('Contrat d\'enrôlement ouvert - utilisez "Enregistrer en PDF" pour le sauvegarder', 'success', 5000);
    } catch (error) {
      console.error('Erreur:', error);
      showAlert('Erreur lors de la génération du contrat', 'error');
    } finally {
      setIsGeneratingDocs(false);
      setGeneratedDocType('');
    }
  };

  // Télécharger tous les documents
  const downloadAllDocuments = () => {
    generateBordereau();
    setTimeout(() => {
      generateContrat();
    }, 2000);
  };
  
  // Validation du formulaire
  const validateForm = () => {
    const errors = [];
    
    if (!formData.nom.trim()) errors.push('Le nom de l\'entité est requis');
    if (!formData.adresse.trim()) errors.push('L\'adresse est requise');
    if (!formData.telephone.trim()) errors.push('Le téléphone est requis');
    if (!formData.email.trim()) errors.push('L\'email de l\'entité est requis');
    if (!formData.admin_email.trim()) errors.push('L\'email de l\'administrateur est requis');
    if (!formData.admin_lname.trim()) errors.push('Le nom de l\'administrateur est requis');
    if (!formData.admin_fname.trim()) errors.push('Le prénom de l\'administrateur est requis');
    if (!formData.admin_phone.trim()) errors.push('Le téléphone de l\'administrateur est requis');
    if (!formData.admin_fonction.trim()) errors.push('La fonction de l\'administrateur est requise');
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('L\'email de l\'entité n\'est pas valide');
    }
    
    if (formData.admin_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      errors.push('L\'email de l\'administrateur n\'est pas valide');
    }
    
    return errors;
  };
  
  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      showAlert(errors.join(', '), 'error');
      return;
    }
    
    setSaving(true);
    setUploadProgress(0);
    setIsUploading(true);
    
    try {
      const token = getAuthToken();
      if (!token) {
        showAlert('Session expirée. Veuillez vous reconnecter.', 'error');
        setSaving(false);
        setIsUploading(false);
        return;
      }
      
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      const apiData = {
        nom: formData.nom.trim(),
        adresse: formData.adresse.trim(),
        telephone: formData.telephone.trim(),
        email: formData.email.trim(),
        country_code: formData.country_code,
        admin_email: formData.admin_email.trim(),
        admin_lname: formData.admin_lname.trim(),
        pays: formData.pays.trim(),
        admin_fname: formData.admin_fname.trim(),
        admin_phone: formData.admin_phone.trim(),
        admin_fonction: formData.admin_fonction.trim()
      };
      
      const response = await fetch(`${API_BASE_URL}/entites`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(apiData),
        mode: 'cors',
        credentials: 'include'
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Erreur HTTP ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.code === 201) {
        setCreatedEntity(result.entite);
        setCreatedAdmin(result.admin);
        setGeneratedUsername(result.username);
        setGeneratedPassword(result.password);
        setShowSuccessModal(true);
        
        showNotification('Entité créée avec succès !', 'success', 5000);
      } else {
        throw new Error(result.message || 'Erreur lors de la création');
      }
      
    } catch (error) {
      console.error('Erreur:', error);
      showAlert(`Erreur: ${error.message}`, 'error');
    } finally {
      setSaving(false);
      setIsUploading(false);
    }
  };
  
  useEffect(() => {
    fetchCountries();
    genererCodeEntite();
  }, [fetchCountries, formData.type]);

  // Reste du JSX inchangé...
  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4" style={{ background: 'ghostwhite', minHeight: '100vh' }}>
        {/* Notifications Toast */}
        <ToastContainer className="p-3" style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          {notifications.map((notification) => (
            <Toast key={notification.id} show={notification.show} onClose={() => {}} delay={5000} autohide>
              <Toast.Header className={`bg-${notification.type === 'success' ? 'success' : notification.type === 'error' ? 'danger' : 'info'} text-white`}>
                {notification.type === 'success' ? <CheckCircle className="me-2" /> :
                 notification.type === 'error' ? <XCircle className="me-2" /> : <ExclamationCircle className="me-2" />}
                <strong className="me-auto">{notification.type === 'success' ? 'Succès' : notification.type === 'error' ? 'Erreur' : 'Information'}</strong>
              </Toast.Header>
              <Toast.Body>{notification.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {/* Alerte Bootstrap */}
        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert variant={alertMessage.type} dismissible onClose={() => setAlertMessage({ show: false, message: '', type: 'info' })} className="shadow-lg border-0">
              <div dangerouslySetInnerHTML={{ __html: alertMessage.message }} />
            </Alert>
          </div>
        )}

        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h1 className="h2 mb-2 text-gradient-primary">
                    <BuildingAdd className="me-2" />
                    Création d'entité
                  </h1>
                  <p className="text-muted mb-0">Enregistrez une nouvelle entité</p>
                </div>
                <Button variant="outline-secondary" as={Link} to="/superAdmin/Entites" className="rounded-pill px-4">
                  <ArrowLeft className="me-2" />
                  Retour
                </Button>
              </div>
            </Col>
          </Row>

          {/* Upload Progress Bar */}
          {isUploading && (
            <Row className="mb-4">
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Création en cours...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <ProgressBar now={uploadProgress} variant="success" animated className="rounded-pill" />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <form onSubmit={handleSubmit}>
            <Row className="g-4">
              <Col lg={8}>
                {/* Informations générales */}
                <Card className="border-0 shadow-lg mb-4 hover-card">
                  <Card.Header className="bg-gradient-primary text-white py-3">
                    <h6 className="mb-0 d-flex align-items-center">
                      <Building className="me-2" />
                      Informations générales
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3">


                       <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Pays <span className="text-danger">*</span>
                          </Form.Label>
                          {loadingCountries ? (
                            <div className="d-flex align-items-center">
                              <Spinner animation="border" size="sm" className="me-2" />
                              <span className="text-muted">Chargement...</span>
                            </div>
                          ) : (
                            <Form.Select name="pays" value={formData.pays} onChange={handlePaysChange} required>
                              <option value="">Sélectionnez un pays</option>
                              {countries.map((country) => (
                                <option key={country.id} value={country.name_fr}>{country.name_fr}</option>
                              ))}
                            </Form.Select>
                          )}
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Devise
                          </Form.Label>
                          <InputGroup>
                            <Form.Control value={`${selectedCountry.currency_code} - ${selectedCountry.currency_name}`} readOnly disabled />
                            <InputGroup.Text>{selectedCountry.currency_symbol}</InputGroup.Text>
                          </InputGroup>
                        </Form.Group>
                      </Col>


                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Type d'entité <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="row g-3">
                            {typesEntite.map(type => (
                              <div key={type.value} className="col-12 col-sm-6 col-lg-4">
                                <div
                                  className={`p-3 border rounded-3 cursor-pointer transition-all h-100 ${
                                    formData.type === type.value ? 'border-primary bg-primary-subtle shadow-sm' : 'border-light bg-light'
                                  }`}
                                  onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                                >
                                  <div className="d-flex align-items-center">
                                    <div className={`bg-${formData.type === type.value ? 'primary' : 'secondary'} bg-opacity-10 rounded-circle p-2 me-3`}>
                                      {React.cloneElement(type.icon, { className: `text-${formData.type === type.value ? 'primary' : 'secondary'}`, size: 20 })}
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="fw-semibold">{type.label}</div>
                                      <small className="text-muted">{type.description}</small>
                                    </div>
                                    {formData.type === type.value && <CheckCircle className="text-primary" size={18} />}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Form.Group>
                      </Col>
                      
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Nom complet <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup>
                            <InputGroup.Text><Building size={16} /></InputGroup.Text>
                            <Form.Control
                              type="text"
                              name="nom"
                              value={formData.nom}
                              onChange={handleChange}
                              placeholder="Ex: MTECH SA"
                              required
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Adresse <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="adresse"
                            value={formData.adresse}
                            onChange={handleChange}
                            placeholder="Adresse complète"
                            required
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Ville
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="ville"
                            value={formData.ville}
                            onChange={handleChange}
                            placeholder="Ex: Libreville"
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
  <Form.Label className="fw-semibold">
    Téléphone <span className="text-danger">*</span>
  </Form.Label>
  <InputGroup>
    <InputGroup.Text><Telephone size={14} /></InputGroup.Text>
    <Form.Control
      type="tel"  // ou "tel"
      name="telephone"
      value={formData.telephone}
      onChange={(e) => {
        // Ne garder que les chiffres
        const onlyNumbers = e.target.value.replace(/\D/g, '');
        handleChange({
          ...e,
          target: { ...e.target, name: 'telephone', value: onlyNumbers }
        });
      }}
      placeholder="XXX XXX XXX"
      required
    />
  </InputGroup>
</Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Email <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup>
                            <InputGroup.Text><Envelope size={14} /></InputGroup.Text>
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="contact@entreprise.com"
                              required
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                      
                     
                    </Row>
                  </Card.Body>
                </Card>
                
                {/* Administrateur */}
                <Card className="border-0 shadow-lg mb-4 hover-card">
                  <Card.Header className="bg-gradient-primary text-white py-3">
                    <h6 className="mb-0 d-flex align-items-center">
                      <Shield className="me-2" />
                      Administrateur principal
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Alert variant="info" className="mb-4 border-0 bg-info-subtle">
                      <ExclamationCircle className="me-2" />
                      Ces informations serviront à créer le compte administrateur de l'entité
                    </Alert>
                    
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="text" name="admin_lname" value={formData.admin_lname} onChange={handleChange} placeholder="Ex: MBOMBA" required />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="text" name="admin_fname" value={formData.admin_fname} onChange={handleChange} placeholder="Ex: Jean" required />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Email <span className="text-danger">*</span></Form.Label>
                          <InputGroup>
                            <InputGroup.Text><Envelope size={14} /></InputGroup.Text>
                            <Form.Control type="email" name="admin_email" value={formData.admin_email} onChange={handleChange} placeholder="admin@entreprise.com" required />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group>
  <Form.Label className="fw-semibold">Téléphone <span className="text-danger">*</span></Form.Label>
  <InputGroup>
    <InputGroup.Text><Telephone size={14} /></InputGroup.Text>
    <Form.Control 
      type="tel" 
      name="admin_phone" 
      value={formData.admin_phone} 
      onChange={(e) => {
        const onlyNumbers = e.target.value.replace(/\D/g, '');
        handleChange({
          ...e,
          target: { ...e.target, name: 'admin_phone', value: onlyNumbers }
        });
      }} 
      placeholder="XXX XXX XXX" 
      required 
    />
  </InputGroup>
</Form.Group>
                      </Col>
                      
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Fonction <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="text" name="admin_fonction" value={formData.admin_fonction} onChange={handleChange} placeholder="Ex: Directeur Général" required />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={4}>
                {/* Logo */}
                <Card className="border-0 shadow-lg mb-4 hover-card">
                  <Card.Header className="bg-gradient-primary text-white py-3">
                    <h6 className="mb-0 d-flex align-items-center">
                      <Image className="me-2" />
                      Logo de l'entité
                    </h6>
                  </Card.Header>
                  <Card.Body className="text-center p-4">
                    {logoPreview ? (
                      <div className="position-relative d-inline-block">
                        <img src={logoPreview} alt="Logo" className="rounded border" style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }} />
                        <Button variant="danger" size="sm" className="position-absolute top-0 end-0 rounded-circle p-1" onClick={removeLogo} style={{ width: '30px', height: '30px' }}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-3 p-5 mb-3">
                        <Upload size={48} className="text-muted mb-3" />
                        <p className="text-muted mb-0">Aucun logo téléchargé</p>
                      </div>
                    )}
                    
                    <Button variant="outline-primary" as="label" htmlFor="logo-upload" className="mt-3 rounded-pill px-4">
                      <Upload className="me-2" />
                      {logoPreview ? 'Changer le logo' : 'Télécharger un logo'}
                    </Button>
                    <Form.Control type="file" id="logo-upload" className="d-none" accept="image/*" onChange={handleLogoUpload} />
                    
                    <Form.Text className="d-block mt-2 text-muted small">
                      Formats: JPG, PNG, GIF (max 5MB)
                    </Form.Text>
                  </Card.Body>
                </Card>
                
                {/* Informations pays */}
                <Card className="border-0 shadow-lg mb-4 hover-card">
                  <Card.Header className="bg-gradient-primary text-white py-3">
                    <h6 className="mb-0 d-flex align-items-center">
                      <Globe2 className="me-2" />
                      Informations pays
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <div className="mb-3">
                      <small className="text-muted d-block">Code ISO</small>
                      <strong>{selectedCountry.code}</strong>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block">Devise</small>
                      <strong>{selectedCountry.currency_code} - {selectedCountry.currency_name} ({selectedCountry.currency_symbol})</strong>
                    </div>
                    <div>
                      <small className="text-muted d-block">Indicatif téléphonique</small>
                      <strong>{selectedCountry.phone_code}</strong>
                    </div>
                    <input type="hidden" name="country_code" value={formData.country_code} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Barre d'actions */}
            <Card className="border-0 shadow-lg mt-4 sticky-bottom">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Check type="checkbox" id="terms-accept" label="Je certifie que les informations fournies sont exactes et complètes" required />
                  
                  <div className="d-flex gap-3">
                    <Button variant="outline-secondary" as={Link} to="/superAdmin/Entites" disabled={saving} className="rounded-pill px-4">
                      <ArrowLeft className="me-2" />
                      Annuler
                    </Button>
                    
                    <Button type="submit" variant="success" disabled={saving} className="rounded-pill px-5">
                      {saving ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                          Création...
                        </>
                      ) : (
                        <>
                          <Save className="me-2" />
                          Créer l'entité
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </form>
        </div>


          <Footer />
      </div>
      
      {/* Modal de succès avec téléchargement des documents - reste inchangé */}
      <Modal show={showSuccessModal} onHide={() => {}} size="lg" centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton className="bg-gradient-success text-white border-0">
          <Modal.Title className="d-flex align-items-center">
            <CheckCircle className="me-2" size={24} />
            Entité créée avec succès !
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* <div className="text-center mb-4">
            <div className="success-icon-circle mx-auto mb-3">
              <CheckCircle size={48} className="text-success" />
            </div>
            <h4 className="mb-2">Félicitations !</h4>
            <p className="text-muted">Votre entité a été créée avec succès</p>
          </div> */}

          {/* Informations de l'entité */}
          {/* <div className="mb-4 p-3 bg-light rounded-3">
            <h6 className="mb-3 text-primary">
              <Building className="me-2" size={16} />
              Informations de l'entité
            </h6>
            <div className="row g-2">
              <div className="col-md-6">
                <small className="text-muted d-block">Nom</small>
                <strong>{createdEntity?.nom || formData.nom}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">ID Entité</small>
                <code>{createdEntity?.entite_id || 'Généré'}</code>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Email</small>
                <strong>{createdEntity?.email || formData.email}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Téléphone</small>
                <strong>{createdEntity?.telephone || formData.telephone}</strong>
              </div>
            </div>
          </div> */}

          {/* Informations de l'administrateur */}
          {/* <div className="mb-4 p-3 bg-light rounded-3">
            <h6 className="mb-3 text-primary">
              <Shield className="me-2" size={16} />
              Informations de l'administrateur
            </h6>
            <div className="row g-2">
              <div className="col-md-6">
                <small className="text-muted d-block">Nom complet</small>
                <strong>{formData.admin_lname} {formData.admin_fname}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Email</small>
                <strong>{formData.admin_email}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Fonction</small>
                <strong>{formData.admin_fonction}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Téléphone</small>
                <strong>{formData.admin_phone}</strong>
              </div>
            </div>
          </div> */}

          {/* Identifiants de connexion */}
          <div className="mb-4 p-3 bg-warning-subtle rounded-3 border border-warning">
            <h6 className="mb-3 text-warning">
              <Key className="me-2" size={16} />
              Identifiants de connexion
            </h6>
            <div className="row g-2">
              <div className="col-md-6">
                <small className="text-muted d-block">Nom d'utilisateur</small>
                <code className="bg-white p-1 rounded">{generatedUsername}</code>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Mot de passe</small>
                <code className="bg-white p-1 rounded">{generatedPassword}</code>
              </div>
            </div>
            <small className="text-danger d-block mt-2">
              ⚠️ Ces identifiants ne seront plus affichés après cette fenêtre.
            </small>
          </div>

          {/* Documents à télécharger */}
          <div className="mb-3">
            <h6 className="mb-3">
              <FileText className="me-2" size={16} />
              Documents officiels à télécharger
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="document-card p-3 rounded-3 border text-center h-100">
                  <FilePdf size={40} className="text-danger mb-2" />
                  <h6 className="mb-1">Bordereau d'enrôlement</h6>
                  <p className="text-muted small mb-2">
                    Fiche d'identité de l'entité<br/>
                    Informations de l'administrateur<br/>
                    Récapitulatif des informations
                  </p>
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={generateBordereau}
                    disabled={isGeneratingDocs && generatedDocType === 'bordereau'}
                    className="rounded-pill px-3"
                  >
                    {isGeneratingDocs && generatedDocType === 'bordereau' ? (
                      <Spinner as="span" animation="border" size="sm" className="me-1" />
                    ) : (
                      <Download size={14} className="me-1" />
                    )}
                    Générer
                  </Button>
                </div>
              </div>
              <div className="col-md-6">
                <div className="document-card p-3 rounded-3 border text-center h-100">
                  <FileText size={40} className="text-primary mb-2" />
                  <h6 className="mb-1">Contrat d'enrôlement</h6>
                  <p className="text-muted small mb-2">
                    Conditions générales<br/>
                    Engagement de l'entité<br/>
                    Politique de confidentialité
                  </p>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={generateContrat}
                    disabled={isGeneratingDocs && generatedDocType === 'contrat'}
                    className="rounded-pill px-3"
                  >
                    {isGeneratingDocs && generatedDocType === 'contrat' ? (
                      <Spinner as="span" animation="border" size="sm" className="me-1" />
                    ) : (
                      <Download size={14} className="me-1" />
                    )}
                    Générer
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="alert alert-info mt-3">
            <ExclamationCircle className="me-2" size={16} />
            <small>
              Ces documents officiels s'ouvriront dans une nouvelle fenêtre. Utilisez "Ctrl+P" ou "Imprimer" puis "Enregistrer en PDF".
            </small>
          </div> */}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/superAdmin/Entites');
            }}
            className="rounded-pill px-4"
          >
            Terminer
          </Button>
          {/* <Button 
            variant="success" 
            onClick={downloadAllDocuments}
            disabled={isGeneratingDocs}
            className="rounded-pill px-4"
          >
            <Download className="me-2" />
            Générer tous les documents
          </Button> */}
        </Modal.Footer>
      </Modal>
      
    
    
      
      <style>{`
        .text-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .bg-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .bg-gradient-success {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
        }
        
        .bg-primary-subtle {
          background-color: rgba(102, 126, 234, 0.1);
        }
        
        .bg-info-subtle {
          background-color: rgba(13, 202, 240, 0.1);
        }
        
        .bg-warning-subtle {
          background-color: rgba(255, 193, 7, 0.1);
        }
        
        .border-dashed {
          border: 2px dashed #dee2e6;
          transition: all 0.3s ease;
        }
        
        .border-dashed:hover {
          border-color: #667eea;
          background-color: rgba(102, 126, 234, 0.05);
        }
        
        .hover-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .hover-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }
        
        .document-card {
          transition: all 0.2s ease;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }
        
        .document-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border-color: #667eea !important;
        }
        
        .success-icon-circle {
          width: 80px;
          height: 80px;
          background: rgba(40, 167, 69, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid rgba(40, 167, 69, 0.2);
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .transition-all {
          transition: all 0.2s ease;
        }
        
        .sticky-bottom {
          position: sticky;
          bottom: 0;
          z-index: 1000;
        }
        
        .rounded-pill {
          border-radius: 50px !important;
        }
        
        code {
          font-family: 'Courier New', monospace;
          font-size: 0.9rem;
        }





 
      `}
      </style>



  
    </React.Fragment>
  );
}